import { Plus } from '@timelyapp/icons/solid-icons';
import type {
  ConnectionSettings,
  CustomFieldMapping,
  ProviderSetupField,
} from '@timelyapp/integration-cloud-api-spec';
import {
  Box,
  Button,
  type ComboboxMenuItem,
  DropdownCombobox,
  Skeleton,
  Tag,
  Text,
} from '@timelyapp/react';
import React, { useMemo } from 'react';
import { truncateText } from 'utils/text-helper';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';

const MAX_CHIP_CHARS = 22;
const ELLIPSIS = '...';

type TargetResourceType = CustomFieldMapping['target_resource_type'];
type CustomFieldMappings = NonNullable<ConnectionSettings['custom_field_mappings']>;

interface Candidate {
  id: string;
  label: string;
  description?: string;
  mapping: CustomFieldMapping;
}

interface Props {
  connectionId: string;
  targetResourceType: TargetResourceType;
  value: CustomFieldMappings | undefined;
  onChange: (mappings: CustomFieldMappings) => void;
  disabled?: boolean;
}

function buildCandidatesById(
  formFields: ProviderSetupField[] | undefined,
  targetResourceType: TargetResourceType
): Map<string, Candidate> {
  const candidatesById = new Map<string, Candidate>();

  formFields?.forEach(field => {
    const optionValue = field.options?.[0]?.value as CustomFieldMapping | undefined;
    if (!optionValue || optionValue.target_resource_type !== targetResourceType) return;

    candidatesById.set(optionValue.id, {
      id: optionValue.id,
      label: field.label,
      description: field.description,
      mapping: optionValue,
    });
  });

  return candidatesById;
}

export const IntegrationsSharedCustomFieldMappings: React.FC<Props> = ({
  connectionId,
  targetResourceType,
  value,
  onChange,
  disabled,
}) => {
  const { data, isLoading, isError, refetch } =
    ticApi.useGetConnectionProviderCustomSettingsFieldsQuery(
      { connectionId },
      { skip: !connectionId }
    );

  const targetMappings = useMemo<CustomFieldMappings>(
    () => (value ?? []).filter(mapping => mapping.target_resource_type === targetResourceType),
    [value, targetResourceType]
  );

  const candidatesById = useMemo(
    () => buildCandidatesById(data?.form_fields, targetResourceType),
    [data, targetResourceType]
  );

  const selectedIds = useMemo(
    () => new Set(targetMappings.map(mapping => mapping.id)),
    [targetMappings]
  );

  const addableItems = useMemo<ComboboxMenuItem[]>(() => {
    return Array.from(candidatesById.values())
      .filter(candidate => !selectedIds.has(candidate.id))
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(candidate => ({
        type: 'item',
        key: candidate.id,
        value: candidate.id,
        label: candidate.label,
        description: candidate.description,
      }));
  }, [candidatesById, selectedIds]);

  const hasCandidates = candidatesById.size > 0;
  const hasSavedMappings = targetMappings.length > 0;

  if (!isLoading && !isError && !hasCandidates && !hasSavedMappings) return null;

  const removeById = (id: string) => {
    onChange((value ?? []).filter(mapping => mapping.id !== id));
  };

  const addById = (id: string) => {
    const candidate = candidatesById.get(id);
    if (!candidate) return;

    // Keep non-client mappings intact so a future project editor can share the same root array.
    onChange([...(value ?? []), candidate.mapping]);
  };

  const renderMappingTag = (mapping: CustomFieldMappings[number]) => {
    const onDismiss = disabled ? undefined : () => removeById(mapping.id);

    if (isLoading) {
      return (
        <Tag
          key={mapping.id}
          variant="subtle"
          tooltip={
            <>
              Saved field, waiting for Jira
              <br />
              {mapping.field_id}
              <br />
              {mapping.id}
            </>
          }
          onDismiss={onDismiss}
        >
          Loading Jira field...
        </Tag>
      );
    }

    const candidate = candidatesById.get(mapping.id);
    if (candidate) {
      return (
        <Tag
          key={mapping.id}
          tooltip={
            candidate.description ? (
              <>
                {candidate.label}
                <br />
                {candidate.description}
              </>
            ) : (
              candidate.label
            )
          }
          onDismiss={onDismiss}
        >
          {truncateText(candidate.label, MAX_CHIP_CHARS, ELLIPSIS)}
        </Tag>
      );
    }

    const isGone = !isError && !!data;
    return (
      <Tag
        key={mapping.id}
        variant={isGone ? 'alert' : 'subtle'}
        tooltip={
          <>
            {isGone ? 'No longer available in Jira' : 'Saved field, details unavailable'}
            <br />
            {mapping.field_id}
            <br />
            {mapping.id}
          </>
        }
        onDismiss={onDismiss}
      >
        {isGone ? 'Deleted Jira field' : 'Saved Jira field'}
      </Tag>
    );
  };

  return (
    <Box.Row gap="medium" alignItems="center" flexWrap="wrap">
      {isLoading && !hasSavedMappings ? (
        <Skeleton.Box key="loading-fields" width="120px" height="24px" borderRadius="medium" />
      ) : (
        targetMappings.map(renderMappingTag)
      )}

      {addableItems.length > 0 && (
        <DropdownCombobox
          trigger={
            <Button priority="xlow" size="xsmall" disabled={disabled}>
              <Plus /> Add custom field
            </Button>
          }
          items={addableItems}
          onSelect={({ value: pickedId }) => addById(pickedId)}
        />
      )}

      {isError && (
        <Box.Row gap="small" alignItems="center">
          <Text>Couldn't load fields.</Text>
          <Button priority="xlow" size="xsmall" onClick={() => refetch()} disabled={disabled}>
            Retry
          </Button>
        </Box.Row>
      )}
    </Box.Row>
  );
};
